import { getValue } from './transform';

/**
 * The message a document travels in, built field by field.
 *
 * A downstream consumer often expects the document inside an envelope of its own: who it is for,
 * when it was sent, what kind of event it is, the document itself as text. None of that is known
 * to this code. Each field says where its value comes from, and a rule can change a field when a
 * condition holds, such as a bill older than a day going out as a different event.
 */
export const WRAP_RESOURCE = 'wrap.json';

export type WrapSource =
  | { from: 'value'; value: string }
  | { from: 'field'; paths: string[]; otherwise?: string }
  | { from: 'tenant'; detail: string }
  | { from: 'now' }
  | { from: 'id' }
  | { from: 'document' };

export interface WrapField {
  name: string;
  source: WrapSource;
}

export interface WrapRule {
  when: { field: string; olderThanHours?: number; equals?: string[] };
  set: Record<string, string>;
}

export interface Wrap {
  fields: WrapField[];
  rules?: WrapRule[];
}

export interface WrapContext {
  tenant?: { id: string; name: string; details?: Record<string, string> };
  now: Date;
  newId: () => string;
}

const present = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

const valueOf = (source: WrapSource, document: Record<string, unknown>, context: WrapContext): unknown => {
  switch (source.from) {
    case 'value':
      return source.value;
    case 'field':
      return source.paths.map((path) => getValue(document, path)).find(present) ?? source.otherwise;
    case 'tenant':
      if (source.detail === 'id') return context.tenant?.id;
      if (source.detail === 'name') return context.tenant?.name;
      return context.tenant?.details?.[source.detail];
    case 'now':
      return context.now.toISOString();
    case 'id':
      return context.newId();
    case 'document':
      return JSON.stringify(document);
  }
};

const holds = (rule: WrapRule, document: Record<string, unknown>, now: Date): boolean => {
  const held = getValue(document, rule.when.field);
  if (!present(held)) return false;
  if (
    rule.when.equals?.length &&
    !rule.when.equals.some((value) => String(held).toLowerCase() === value.toLowerCase())
  ) {
    return false;
  }
  if (rule.when.olderThanHours !== undefined) {
    const at = new Date(String(held)).getTime();
    if (Number.isNaN(at)) return false;
    if (now.getTime() - at <= rule.when.olderThanHours * 3_600_000) return false;
  }
  return true;
};

export const applyWrap = (
  config: Wrap | undefined,
  document: Record<string, unknown>,
  context: WrapContext,
): Record<string, unknown> => {
  if (!config?.fields?.length) return document;

  const wrapped: Record<string, unknown> = {};
  for (const field of config.fields) {
    const value = valueOf(field.source, document, context);
    if (present(value)) wrapped[field.name] = value;
  }
  for (const rule of config.rules ?? []) {
    if (holds(rule, document, context.now)) Object.assign(wrapped, rule.set);
  }
  return wrapped;
};

/** Why this wrapper cannot be saved, in the author's words, or nothing when it is fine. */
export const wrapFault = (config: Wrap): string | undefined => {
  if (!config.fields?.length) return 'Add at least one field to the message.';
  const names = new Set<string>();
  for (const field of config.fields) {
    if (!field.name?.trim()) return 'Every field needs a name.';
    if (names.has(field.name)) return `"${field.name}" appears twice.`;
    names.add(field.name);
    const source = field.source;
    if (source.from === 'field' && !source.paths?.filter(Boolean).length)
      return `Say which field "${field.name}" is read from.`;
    if (source.from === 'tenant' && !source.detail?.trim()) return `Say which tenant detail "${field.name}" uses.`;
    if (source.from === 'value' && typeof source.value !== 'string') return `Give "${field.name}" a value.`;
  }
  for (const rule of config.rules ?? []) {
    if (!rule.when?.field?.trim()) return 'Every rule needs a field to look at.';
    if (rule.when.olderThanHours === undefined && !rule.when.equals?.length) {
      return `The rule on "${rule.when.field}" needs an age or a value to compare with.`;
    }
    if (rule.when.olderThanHours !== undefined && !(rule.when.olderThanHours > 0)) {
      return 'An age must be a number of hours above zero.';
    }
    if (!Object.keys(rule.set ?? {}).length) return `The rule on "${rule.when.field}" changes nothing.`;
  }
  return undefined;
};
